import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, AlertTriangle, FileText, Layers, ChevronRight, TrendingUp, Coins, Building2, Lock } from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { notify } from "../lib/notify";

// =========== SG: Breach Playbook ===========
export function BreachPlaybook() {
  const [stages, setStages] = useState([]);
  useEffect(() => { api.get("/breach-playbook").then(r => setStages(r.data.stages)); }, []);

  return (
    <div data-testid="page-breach-playbook">
      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <Link to="/singapore" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Singapore Hub</Link>
          <div className="overline mt-6 flex items-center gap-2"><AlertTriangle size={11} /> Multi-regulator incident response</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">Breach Playbook.</h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg leading-relaxed">
            Coordinated escalation across MAS, PDPC and CSA. Who to call first, what to file, and the evidence pack you need at every stage from T+0 to T+30 days.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-rp">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            {[
              { label: "MAS — material cyber breach", clock: "1 hour", desc: "MAS TRM §15 · notify within 1 hour of confirming materiality" },
              { label: "PDPC — notifiable personal data breach", clock: "3 days", desc: "≥500 individuals OR significant harm" },
              { label: "CSA — CII compromise", clock: "Per directive", desc: "Cybersecurity Act · CCoP 2.0 timelines" },
            ].map(c => (
              <div key={c.label} className="rp-card p-6">
                <div className="overline">{c.label}</div>
                <div className="font-display text-4xl font-bold mt-3" style={{ color: "var(--rp-accent)" }}>{c.clock}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

          <div className="overline mb-3">The clock</div>
          <h2 className="font-display text-4xl font-semibold mb-10">From detection to closure.</h2>

          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-slate-200 hidden md:block" />
            <div className="space-y-6">
              {stages.map((s, i) => (
                <div key={s.stage} className="relative md:pl-16" data-testid={`stage-${i}`}>
                  <div className="hidden md:flex absolute left-0 top-3 w-12 h-12 rounded-sm items-center justify-center font-mono font-semibold text-sm" style={{ background: "var(--rp-primary)", color: "#fff" }}>{s.stage}</div>
                  <div className="rp-card p-6">
                    <div className="overline">{s.stage} · {s.label}</div>
                    <ul className="mt-4 space-y-2">
                      {s.items.map((it, j) => (
                        <li key={it} className="text-sm text-slate-700 leading-relaxed flex gap-3">
                          <span className="font-mono text-xs text-slate-400">{String(j + 1).padStart(2, "0")}</span>
                          {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 rp-card p-8 rp-surface-subtle">
            <div className="overline">Disclaimer</div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">Indicative playbook — verify against current MAS, PDPC and CSA guidance and your own legal counsel. RiskPedia content is educational only.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

// =========== SG: Templates Library ===========
export function TemplatesLibrary() {
  const [tpl, setTpl] = useState([]);
  const [tier, setTier] = useState("all");
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => { api.get("/templates").then(r => setTpl(r.data.templates)); }, []);

  const filtered = tier === "all" ? tpl : tpl.filter(t => t.tier === tier);

  const download = async (t) => {
    if (!user) { notify.info("Sign in to download templates."); nav("/login?next=/singapore/templates"); return; }
    if (t.tier === "Pro" && !user.is_pro) {
      notify.error("Pro required to download this template.");
      nav("/pricing");
      return;
    }
    try {
      const r = await api.get(`/templates/${t.id}/download`, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const fname = (cd.match(/filename="?([^"]+)"?/) || [])[1] || `${t.id}.bin`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
      notify.success(`${t.name} downloaded.`);
    } catch (e) {
      if (e.response?.status === 402) {
        notify.error("Pro required."); nav("/pricing");
      } else {
        notify.error("Could not download.");
      }
    }
  };

  return (
    <div data-testid="page-templates">
      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <Link to="/singapore" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Singapore Hub</Link>
          <div className="overline mt-6 flex items-center gap-2"><FileText size={11} /> Singapore-specific templates</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">Templates Library.</h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg leading-relaxed">
            RoPA · IR plan · BCP · DPIA · outsourcing register · breach log · board notes · DPO charter · model cards.<br />Six free, six Pro.
          </p>
          <div className="mt-8 flex gap-2">
            <button onClick={() => setTier("all")} data-active={tier === "all"} className="persona-btn">All ({tpl.length})</button>
            <button onClick={() => setTier("Free")} data-active={tier === "Free"} className="persona-btn">Free</button>
            <button onClick={() => setTier("Pro")} data-active={tier === "Pro"} className="persona-btn">Pro</button>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-rp">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {filtered.map(t => (
              <div key={t.id} className="rp-surface p-6 flex flex-col" data-testid={`tpl-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div className="overline-muted">{t.regulator}</div>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm" style={{ background: t.tier === "Pro" ? "var(--rp-accent)" : "var(--rp-bg-subtle)", color: t.tier === "Pro" ? "#fff" : "var(--rp-ink-2)", border: t.tier === "Pro" ? "none" : "1px solid var(--rp-border)" }}>{t.tier}</span>
                </div>
                <div className="font-display text-lg font-semibold mt-3">{t.name}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed flex-1">{t.desc}</p>
                <div className="mt-5 flex items-center justify-between border-t border-slate-200 pt-4">
                  <span className="font-mono text-[10px] text-slate-500">{t.format}</span>
                  <button className="text-xs font-medium inline-flex items-center gap-1" style={{ color: "var(--rp-accent)" }} onClick={() => download(t)} data-testid={`download-${t.id}`}>
                    {t.tier === "Pro" && (!user || !user.is_pro) ? <><Lock size={12} /> Unlock</> : <><Download size={12} /> Download</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// =========== SG: Regulator Overlap Matrix ===========
export function OverlapMatrix() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/overlap-matrix").then(r => setData(r.data)); }, []);

  return (
    <div data-testid="page-overlap">
      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <Link to="/singapore" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Singapore Hub</Link>
          <div className="overline mt-6 flex items-center gap-2"><Layers size={11} /> Where rules align, conflict and layer</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">Regulator Overlap Matrix.</h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg leading-relaxed">
            MAS TRM × MAS Notice 655 × PDPA × CSA × MindForge — mapped across 10 control dimensions.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-rp">
          {data && (
            <div className="overflow-x-auto rp-card">
              <table className="w-full border-collapse min-w-[1100px]" data-testid="overlap-table">
                <thead>
                  <tr style={{ background: "var(--rp-bg-subtle)" }}>
                    <th className="text-left p-4 border-b border-slate-200">
                      <div className="overline-muted">Dimension</div>
                    </th>
                    {data.regulators.map(r => (
                      <th key={r.name} className="text-left p-4 border-b border-slate-200 align-top min-w-[180px]">
                        <div className="font-display text-base font-semibold">{r.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono mt-1">{r.scope}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.dimensions.map((dim, i) => (
                    <tr key={dim} className="border-b border-slate-200">
                      <td className="p-4 font-medium text-sm align-top" style={{ background: "var(--rp-bg-subtle)" }}>{dim}</td>
                      {data.regulators.map(r => (
                        <td key={r.name} className="p-4 text-sm text-slate-700 leading-relaxed align-top">{r.rows[i]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-6">Scroll horizontally to compare. Cells show the regulator's specific requirement for each dimension. — indicates 'not directly addressed'.</p>
        </div>
      </section>
    </div>
  );
}

// =========== Money Flow Detail page ===========
export function MoneyFlowDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/money-flow/${slug}`).then(r => setData(r.data)).catch(() => setErr(true)); }, [slug]);

  if (err) return (
    <div className="container-rp section text-center">
      <h1 className="font-display text-4xl font-bold">Not found</h1>
      <Link to="/money-flow" className="btn-primary mt-6 inline-flex">Money Flow Hub <ChevronRight size={14} /></Link>
    </div>
  );
  if (!data) return <div className="container-rp py-24 text-slate-400">Loading…</div>;

  const ICONS = { nexus: TrendingUp, dpt: Coins, guardian: Building2, stablecoins: Coins };
  const Icon = ICONS[slug] || TrendingUp;

  return (
    <div data-testid={`page-mf-${slug}`}>
      <section className="force-dark relative overflow-hidden">
        <div className="bg-grid-dark absolute inset-0 opacity-60" />
        <div className="container-rp py-16 lg:py-24 relative">
          <button onClick={() => nav(-1)} className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1 mb-6"><ArrowLeft size={12} /> Money Flow</button>
          <div className="flex items-start gap-5">
            <div className="w-12 h-12 rounded-sm flex items-center justify-center" style={{ background: "rgba(255,255,255,0.08)" }}><Icon size={22} /></div>
            <div>
              <div className="overline">{data.kicker}</div>
              <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">{data.title}</h1>
            </div>
          </div>
          <p className="text-slate-300 mt-6 max-w-3xl text-lg leading-relaxed">{data.summary}</p>
        </div>
      </section>

      <section className="section">
        <div className="container-rp grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="space-y-10">
              {data.blocks.map((b, i) => (
                <div key={b.title}>
                  <div className="overline">{String(i + 1).padStart(2, "0")}</div>
                  <h2 className="font-display text-3xl font-semibold mt-2">{b.title}</h2>
                  <p className="text-slate-600 mt-4 leading-relaxed">{b.body}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 rp-card p-6 rp-surface-subtle">
              <div className="overline">Educational disclaimer</div>
              <p className="text-sm text-slate-600 mt-2">Not legal, regulatory or investment advice. Always confirm with MAS / BIS publications.</p>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rp-card p-6 sticky top-24">
              <div className="overline">Key facts</div>
              <dl className="mt-4 space-y-3 text-sm">
                {data.facts.map((f) => (
                  <div key={f.k} className="border-b border-slate-200 pb-2">
                    <dt className="text-xs uppercase tracking-wider text-slate-500">{f.k}</dt>
                    <dd className="font-medium mt-1">{f.v}</dd>
                  </div>
                ))}
              </dl>
              <Link to="/money-flow" className="btn-outline w-full justify-center mt-6">Money Flow Hub <ChevronRight size={14} /></Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
